import { FronteggContext } from '@frontegg/rest-api';
import { Axios } from 'src/lib';
import {
  listPromptsConfig,
  listLlmsConfig,
  listPromptScreensConfig,
  getPromptConfig,
  createPromptConfig,
  createPromptScreenConfig,
  updatePromptScreenConfig,
} from '../lib/promptsv2.url';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import {
  listPromptsSlice,
  listPromptsLoadingSlice,
  listLlmsSlice,
  listLlmsLoadingSlice,
  listPromptScreensSlice,
  listPromptScreensLoadingSlice,
  getPromptSlice,
  getPromptLoadingSlice,
  resetPromptsSlice,
} from '../slices/prompts.slice';

export const listPromptsAction = (filters = {}, org_uuid = null) => {
  return async (dispatch) => {
    dispatch(listPromptsLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      if (org_uuid) {
        headers['Impersonate-Org-UUID'] = `${org_uuid}`;
      }
      const params = {
        ...filters,
      };
      const { url } = listPromptsConfig();
      const result = await Axios.get(url, { headers, params });
      if (result?.status === 200) {
        dispatch(listPromptsSlice(result?.data));
      }
    } catch (e) {
      showToastError('Failed to load prompts');
    } finally {
      dispatch(listPromptsLoadingSlice(false));
    }
  };
};

export const getPromptAction = (promptId) => {
  return async (dispatch) => {
    dispatch(getPromptLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = getPromptConfig(promptId);
      const result = await Axios.get(url, { headers });
      if (result?.status === 200) {
        dispatch(getPromptSlice(result?.data));
      }
    } catch (e) {
      showToastError('Failed to load prompt');
    } finally {
      dispatch(getPromptLoadingSlice(false));
    }
  };
};

export const updatePromptAction = (promptId, payload, successCallback) => {
  return async (dispatch) => {
    dispatch(getPromptLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = getPromptConfig(promptId);
      const result = await Axios.put(url, payload, { headers });
      if (result?.status === 200) {
        dispatch(getPromptSlice(result?.data?.prompt));
        if (successCallback) {
          showToastSuccess('Successfully updated prompt');
          successCallback();
        }
      }
    } catch (e) {
      showToastError('Failed to update prompt');
    } finally {
      dispatch(getPromptLoadingSlice(false));
    }
  };
};

export const createPromptAction = (payload, successCallback) => {
  return async (dispatch) => {
    dispatch(getPromptLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = createPromptConfig();
      const result = await Axios.post(url, payload, { headers });
      if (result?.status === 201) {
        dispatch(getPromptSlice(result?.data?.prompt));
        if (successCallback) {
          showToastSuccess('Successfully created prompt');
          successCallback();
        }
      }
    } catch (e) {
      showToastError('Failed to create prompt');
    } finally {
      dispatch(getPromptLoadingSlice(false));
    }
  };
};

export const listLlms = (filters = {}) => {
  return async (dispatch) => {
    dispatch(listLlmsLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const params = {
        ...filters,
      };
      const { url } = listLlmsConfig();
      const result = await Axios.get(url, { headers, params });
      if (result?.status === 200) {
        dispatch(listLlmsSlice(result?.data));
      }
    } catch (e) {
      showToastError('Failed to load llms');
    } finally {
      dispatch(listLlmsLoadingSlice(false));
    }
  };
};

export const listPromptScreens = (
  condition_id = null,
  specialty_id = null,
  scope = null
) => {
  return async (dispatch) => {
    dispatch(listPromptScreensLoadingSlice(true));
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = listPromptScreensConfig();
      const params = {
        condition_id,
        specialty_id,
        scope,
      };
      const result = await Axios.get(url, { headers, params });
      if (result?.status === 200) {
        dispatch(listPromptScreensSlice(result?.data));
      }
    } catch (e) {
      showToastError('Failed to load prompt screens');
    } finally {
      dispatch(listPromptScreensLoadingSlice(false));
    }
  };
};

export const createPromptScreenAction = (payload) => {
  return async (dispatch) => {
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = createPromptScreenConfig();
      const result = await Axios.post(url, payload, { headers });
      if (result?.status === 200 || result?.status === 201) {
        showToastSuccess('Successfully created prompt screen');
        dispatch(listPromptScreens(null, null, payload?.scope ?? null));
        return result;
      }
      showToastError('Failed to create prompt screen');
      throw new Error('create_prompt_screen_failed');
    } catch (e) {
      if (e.message !== 'create_prompt_screen_failed') {
        showToastError('Failed to create prompt screen');
      }
      throw e;
    }
  };
};

export const updatePromptScreenAction = (promptScreenId, payload) => {
  return async (dispatch) => {
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const { url } = updatePromptScreenConfig(promptScreenId);
      const result = await Axios.put(url, payload, { headers });
      if (result?.status === 200) {
        showToastSuccess('Successfully updated prompt screen');
        dispatch(listPromptScreens(null, null, payload?.scope ?? null));
        return result;
      }
      showToastError('Failed to update prompt screen');
      throw new Error('update_prompt_screen_failed');
    } catch (e) {
      if (e.message !== 'update_prompt_screen_failed') {
        showToastError('Failed to update prompt screen');
      }
      throw e;
    }
  };
};

export const resetPromptsAction = () => (dispatch) => {
  dispatch(resetPromptsSlice());
};

// Re-export LLM overrides actions
export {
  listLlmOverridesAction,
  updateLlmOverridesAction,
} from './llmoverrides.action';
